import React, { useEffect, useState } from 'react';
import { config } from '@uhg-abyss/web/tools/config';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { criteria, coveo, registerFields } from '@src/tools/coveo';
import { buildSearchEngine, getOrganizationEndpoints } from '@coveo/headless';
import { useGetCoveoAuthentication } from '@sde/widgets-core/src/common/hooks/useGetCoveoAuthentication';
import { ResultList } from '@src/common/Coveo/ResultList/ResultList';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { StandaloneSearchBox } from '@src/common/Coveo/StandaloneSearchBox';
import { PageLayout } from '@src/common/PageLayout';
import { PageHeader } from '@src/common/PageHeader';
import { useBreadcrumbs } from '@src/state/breadcrumbs';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Link } from '@uhg-abyss/web/ui/Link';
import { SortAndFilterSection } from '@src/common/Coveo/SortAndFilterSection';
import { FilterBox } from '@src/common/Coveo/FilterBox';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { Box } from '@uhg-abyss/web/ui/Box';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { event } from '@uhg-abyss/web/tools/event';
import { MediaQuery } from '@uhg-abyss/web/ui/MediaQuery';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import {
  resultsCountEvent,
  searchEvent,
  searchCompleteEvent,
} from '@src/common/Coveo/events';
import { RenderHtml } from '@src/common/RenderHtml';
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { PageBodyIntro } from '@workshop/common/PageBodyIntro';

const CustomLayout = styled(PageLayout, {
  marginTop: '-52px',
  marginLeft: '0px !important',
  marginRight: '0px !important',
  maxWidth: 'none !important',
  overflow: 'hidden',
  '@media screen and (max-width: 1380px)': {
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
});
const LoadingContainer = styled('div', {
  height: '500px',
});
const CustomLayoutWrapper = styled('div', {});

const CustomPageHeader = styled(PageHeader, {
  boxShadow: 'none !important',
  marginBottom: '0px !important',
});

const CustomAccordion = styled(Accordion, {
  border: 'none',
  '.abyss-accordion-trigger': {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'black',
    border: 'none',
  },
  '.abyss-accordion-content-container': {
    backgroundColor: '$white',
  },
});

const ResultsContainer = styled('div', {
  margin: '15px 0px',
});

const SearchBoxContainer = styled('div', {
  marginBottom: '0px',
});

const SuggestionLinkContainer = styled('div', {
  margin: '20px 0px',
});

const LeftGrid = styled(Grid.Col, {
  borderRight: '1px solid $gray4',
  backgroundColor: '$white',
  marginBottom: '13px',
});

const DrawerButton = styled(Button, {
  float: 'right',
  size: '12px',
  padding: '0px 0px 8px',
});

const Drawermodal = styled(Drawer, {
  '.abyss-modal-content-title': {
    color: '#002677',
  },
});

const FilterIconstyle = styled(IconMaterial, {
  marginRight: '5px',
});

const FilterContainer = styled('div', {
  backgroundColor: '$white',
  height: '100%',
  width: '250px',
  paddingBottom: '20px',
  float: 'right',
  '@media screen and (max-width: 879px)': {
    width: '360px',
    paddingLeft: '16px',
  },
});

const DynamicWidthBox = styled(Box, {
  maxWidth: '1050px',

  '@media screen(min-width: 984px)': {},
  '@media screen(min-width: 744px)': {},
});

export const CoveoSearchPage = () => {
  const { getMessage } = usePPDLagoon();
  const defaultErrorMessage = getMessage('default-error-message');
  const theme = createTheme('uhc', {
    css: {
      '.abyss-theme-provider-root': null,
    },
  });
  const { getRouteParams } = useRouter();
  const params = getRouteParams();
  const searchTerm = Object.values(params).join('');
  const { setBreadcrumbs, breadcrumbs } = useBreadcrumbs();
  const [headlessEngine, setHeadlessEngine] = useState();
  const [isError, setIsError] = useState(false);
  const [facets, setFacets] = useState([]);
  const [isFacetSelected, setIsFacetSelected] = useState(false);
  const [selectedFacets, setSelectedFacets] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [isSearchComplete, setIsSearchComplete] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [authTokenResponse, getCoveoAuth] = useGetCoveoAuthentication({
    onCompleted: (response) => {
      const baseEngine = buildSearchEngine({
        configuration: {
          organizationId: config('COVEO_ORG_ID'),
          organizationEndpoints: getOrganizationEndpoints(
            config('COVEO_ORG_ID')
          ),
          accessToken: response.token,
          analytics: {
            analyticsClientMiddleware: (eventName, payload) => {
              return payload;
            },
          },
          search: {
            preprocessSearchResponseMiddleware: (res) => {
              setTriggers(res.body.triggers);
              event.setData(
                resultsCountEvent({
                  totalCountFiltered: res.body.totalCountFiltered,
                })
              );
              setIsSearchComplete(true);
              return res;
            },
            pipeline: 'PortalSearch',
            searchHub: 'PortalSearch',
          },
        },
      });

      const registeredFieldsEngine = registerFields(baseEngine);
      setHeadlessEngine(registeredFieldsEngine);

      setFacets([
        {
          filterTitle: 'Location',
          controller: coveo.facets.location(registeredFieldsEngine),
        },
        {
          filterTitle: 'Medical Specialty',
          controller: coveo.facets.medicalSpecialty(registeredFieldsEngine),
        },
        {
          filterTitle: 'Plans',
          controller: coveo.facets.plans(registeredFieldsEngine),
        },
        {
          filterTitle: 'Service Area',
          controller: coveo.facets.serviceArea(registeredFieldsEngine),
        },
        {
          filterTitle: 'Resource Type',
          controller: coveo.facets.resourceType(registeredFieldsEngine),
        },
      ]);
    },
    onError: (err) => {
      setIsError(true);
    },
  });

  useEffect(() => {
    getCoveoAuth({ variables: {} });
  }, []);

  const [searchBoxController, setSearchBoxController] = useState({});

  useEffect(() => {
    if (headlessEngine) {
      const controller = coveo.controllers.searchBox(headlessEngine);
      setSearchBoxController(controller);
    }
  }, [headlessEngine]);

  useEffect(() => {
    if (
      searchBoxController &&
      typeof searchBoxController.updateText === 'function' &&
      searchTerm
    ) {
      searchBoxController.updateText(searchTerm);

      if (searchBoxController.state?.value === searchTerm) {
        searchBoxController.submit();
        event.setData(
          searchEvent({
            searchEventTrigger: 'searchboxSubmit',
            searchTerm,
          })
        );
      }
    }
  }, [searchTerm, searchBoxController]);

  useEffect(() => {
    event.setData({ pageName: 'search results' });
    setBreadcrumbs({
      id: 'app-search',
      title: 'Search Results',
      href: '/app-search',
    });
    event('APP_SEARCH_PAGE_LOAD');
  }, []);

  useEffect(() => {
    if (isSearchComplete && headlessEngine) {
      event.setData(
        searchCompleteEvent({
          searchStartIndex: headlessEngine.state.pagination.firstResult,
          searchItemsPerPage: headlessEngine.state.pagination.numberOfResults,
        })
      );
      setIsSearchComplete(false);
      event('COVEO_APP_SEARCH');
    }
  }, [isSearchComplete]);

  const handleUhcLinkClick = () => {
    event('COVEO_NOT_FINDING_CLICK', {
      interactionContext: searchTerm,
    });
    event('EXIT_LINK_NAVIGATE', {
      linkText:
        'Not finding what you are looking for? Try searching UHCProvider.com',
      linkUrl: 'https://uhcprovider.com',
    });
  };

  const isSmallScreen = useMediaQuery('(min-width: 880px)');

  return (
    <ThemeProvider theme={theme}>
      <PageBodyIntro title='Search' breadcrumbs={breadcrumbs} />
      <CustomLayout>
        <CustomLayoutWrapper>
          <Grid>
            <Choose>
              <When condition={!headlessEngine}>
                <Grid.Col span='100%'>
                  <LoadingContainer>
                    <LoadingOverlay
                      loadingTitle='Please Wait'
                      loadingMessage={`searching for ${searchTerm}`}
                      isLoading={authTokenResponse.loading}
                      isOpen={isError}
                      statusMessage={defaultErrorMessage}
                      statusIcon='warning'
                    />
                  </LoadingContainer>
                </Grid.Col>
              </When>
              <Otherwise>
                <MediaQuery largerThan={880}>
                  <LeftGrid span='30%'>
                    <FilterContainer>
                      <Layout.Stack alignItems='left' grow space='10'>
                        <SortAndFilterSection
                          controller={coveo.controllers.sorter(headlessEngine)}
                          criteriaOptions={criteria}
                        />
                        <Divider height='1px' />
                        <CustomAccordion
                          isCollapsible
                          type='multiple'
                          defaultValue={[
                            'Location',
                            'Medical Specialty',
                            'Plans',
                            'Service Area',
                            'Resource Type',
                          ]}
                        >
                          <For each='facet' of={facets}>
                            <FilterBox
                              key={facet.filterTitle}
                              filterTitle={facet.filterTitle}
                              controller={facet.controller}
                              setIsFacetSelected={setIsFacetSelected}
                              setSelectedFacets={setSelectedFacets}
                              selectedFacets={selectedFacets}
                            />
                          </For>
                        </CustomAccordion>
                      </Layout.Stack>
                    </FilterContainer>
                  </LeftGrid>
                </MediaQuery>
                <Grid.Col span={isSmallScreen ? '70%' : '100%'}>
                  <DynamicWidthBox>
                    <ResultsContainer>
                      <SearchBoxContainer>
                        <StandaloneSearchBox
                          setIsFacetSelected={setIsFacetSelected}
                          setSelectedFacets={setSelectedFacets}
                          controller={searchBoxController}
                        />
                      </SearchBoxContainer>
                      <SuggestionLinkContainer>
                        <Text>
                          Not finding what you are looking for? Try searching{' '}
                          <Link
                            href='https://uhcprovider.com'
                            onClick={() => handleUhcLinkClick()}
                          >
                            UHCProvider.com
                          </Link>
                        </Text>
                        <If condition={!isSmallScreen}>
                          <DrawerButton
                            onClick={() => setIsDrawerOpen(true)}
                            aria-haspopup='dialog'
                            variant='tertiary'
                          >
                            <FilterIconstyle
                              icon='filter_list'
                              variant='filled'
                              size='16px'
                            />
                            {'   '}
                            Refine Results
                          </DrawerButton>
                        </If>
                      </SuggestionLinkContainer>
                      <If condition={triggers.length > 0}>
                        <For each='trigger' of={triggers}>
                          <RenderHtml>{trigger.content}</RenderHtml>
                        </For>
                      </If>
                      <ResultList
                        controller={coveo.controllers.resultList(
                          headlessEngine
                        )}
                        searchTerm={searchTerm}
                        headlessEngine={headlessEngine}
                        facets={facets}
                        isFacetSelected={isFacetSelected}
                        setIsFacetSelected={setIsFacetSelected}
                        selectedFacets={selectedFacets}
                        setSelectedFacets={setSelectedFacets}
                      />
                    </ResultsContainer>
                  </DynamicWidthBox>
                </Grid.Col>
                <Drawermodal
                  title='Refine Results'
                  isOpen={isDrawerOpen}
                  onClose={() => setIsDrawerOpen(false)}
                  placement='right'
                  size='$sm'
                  aria-haspopup='dialog'
                >
                  <FilterContainer>
                    <Layout.Stack alignItems='left' grow space='10'>
                      <SortAndFilterSection
                        controller={coveo.controllers.sorter(headlessEngine)}
                        criteriaOptions={criteria}
                      />
                      <Divider height='1px' />
                      <CustomAccordion
                        isCollapsible
                        type='multiple'
                        defaultValue={[
                          'Location',
                          'Medical Specialty',
                          'Plans',
                          'Service Area',
                          'Resource Type',
                        ]}
                      >
                        <For each='facet' of={facets}>
                          <FilterBox
                            key={facet.filterTitle}
                            filterTitle={facet.filterTitle}
                            controller={facet.controller}
                            setIsFacetSelected={setIsFacetSelected}
                            setSelectedFacets={setSelectedFacets}
                            selectedFacets={selectedFacets}
                          />
                        </For>
                      </CustomAccordion>
                    </Layout.Stack>
                  </FilterContainer>
                </Drawermodal>
              </Otherwise>
            </Choose>
          </Grid>
        </CustomLayoutWrapper>
      </CustomLayout>
    </ThemeProvider>
  );
};
